import { getFirestore, collection, addDoc, query, where, orderBy, limit, getDocs, doc, updateDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);
const COLLECTION_NAME = 'support_tickets';

export const SupportService = {
    /**
     * Create a new support ticket.
     * @param {string} userId - The ID of the authenticated user.
     * @param {string} content - The content of the inquiry.
     * @returns {Promise<string>} The ID of the created ticket.
     */
    createTicket: async (userId, content) => {
        if (!userId || !content) throw new Error("Missing userId or content");

        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
            userId,
            content,
            status: 'open',
            createdAt: serverTimestamp(),
            replyContent: null,
            replyAt: null,
            isReadByUser: true // User just wrote it
        });
        return docRef.id;
    },

    /**
     * Get tickets for a specific user.
     * @param {string} userId 
     * @param {number} limitCount 
     * @returns {Promise<Array>} List of tickets.
     */
    getMyTickets: async (userId, limitCount = 20) => {
        const q = query(
            collection(db, COLLECTION_NAME),
            where("userId", "==", userId),
            orderBy("createdAt", "desc"),
            limit(limitCount)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Convert timestamps to Date objects for easier handling
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            replyAt: doc.data().replyAt?.toDate() || null
        })).reverse(); // Return in chronological order for chat view
    },

    /**
     * Subscribe to user's tickets (Real-time updates).
     * @param {string} userId 
     * @param {function} callback 
     * @returns {function} Unsubscribe function
     */
    subscribeMyTickets: (userId, callback) => {
        const q = query(
            collection(db, COLLECTION_NAME),
            where("userId", "==", userId),
            orderBy("createdAt", "desc"),
            limit(50)
        );

        return onSnapshot(q, (snapshot) => {
            const tickets = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date(),
                replyAt: doc.data().replyAt?.toDate() || null
            })).reverse();
            callback(tickets);
        });
    }
};
